// Markens sida i Min jakt: brödsmulor, titelblock och flikrad (DR1 omgång 2, skiss B och B2).
// Desktop: under sajtens TopNav. Mobil: egen rad med tillbaka och markåtgärder (TopNav döljs där).
import React from 'react';
import { ChevronLeft, ExternalLink, MoreHorizontal, Settings, Trash2 } from 'lucide-react';
import type { Mark } from '../../api';
import { TYPE, useTheme } from '../../theme';
import { Badge, Button, Icon, IconButton, Menu, Tabs, type MenuItem, type TabItem } from '../../components/ui';
import { NAV_MAIN_H, NAV_TABS_H } from '../../components/TopNav';
import { fmtHa, markPlace } from './parts';

/**
 * Flikordning efter roll (DR1 §3.2): den som sköter marken ser medlemmar, ekonomi och annonser först, en jägare albumet.
 * Returnerar flikarna i ordning och hur många som ska synas innan "Mer". Flikar som inte finns i `items` hoppas över.
 */
export function orderMarkTabs<T extends { key: string }>(items: T[], canManage: boolean, mob: boolean): { ordered: T[]; maxVisible: number } {
  // P4: Efter jakt syns direkt (fällt vilt efter jakten), inte under Mer.
  const first = canManage ? ['karta', 'kalender', 'gruppchat', 'efterjakt', 'medlemmar', 'ekonomi', 'annonser'] : ['karta', 'kalender', 'gruppchat', 'efterjakt', 'album'];
  const head = first.flatMap((k) => items.filter((t) => t.key === k));
  const ordered = [...head, ...items.filter((t) => !first.includes(t.key))];
  return { ordered, maxVisible: mob ? 4 : head.length };
}

/** Mobilens rad med tillbaka-knapp. */
const MOBILE_BAR_H = 56;

export function MarkHeader<K extends string>({ mark, mob, roleLabel, memberCount, tabs, tab, onTab, maxVisible, onBack, onSettings, onPublicPage, onDelete, notice, subBar }: {
  mark: Mark; mob: boolean;
  /** Ens roll på marken, t.ex. "Markägare". */
  roleLabel?: string;
  memberCount?: number;
  tabs: TabItem<K>[]; tab: K; onTab: (k: K) => void;
  /** Antal flikar som syns innan resten hamnar under "Mer". */
  maxVisible: number;
  onBack: () => void;
  /** Markens uppgifter (Inställningar), om man får redigera. */
  onSettings?: () => void;
  /** Den publika jaktsidan, om marken är publicerad. */
  onPublicPage?: () => void;
  /** Radera marken (bara admin). */
  onDelete?: () => void;
  /** Rad under flikarna, t.ex. fel vid jaktstart. */
  notice?: React.ReactNode;
  /** UX7: rad som ligger fast under flikarna, t.ex. "Alla chattar" i chatten. */
  subBar?: React.ReactNode;
}) {
  const T = useTheme();
  // UX8: på mobilen visas markens namn i den fasta raden när rubriken har skrollats bort (t.ex. i chatten).
  const titleRef = React.useRef<HTMLHeadingElement>(null);
  const [titleHidden, setTitleHidden] = React.useState(false);
  React.useEffect(() => {
    const el = titleRef.current;
    if (!mob || !el || typeof IntersectionObserver === 'undefined') { setTitleHidden(false); return; }
    const io = new IntersectionObserver(([e]) => setTitleHidden(!e.isIntersecting), { rootMargin: `-${MOBILE_BAR_H}px 0px 0px 0px` });
    io.observe(el);
    return () => io.disconnect();
  }, [mob]);
  const sub = [markPlace(mark, !mob) || 'Okänd plats', fmtHa(mark.areaHa), !mob && memberCount ? `${memberCount} medlemmar` : ''].filter(Boolean).join(' · ');
  const actions: MenuItem[] = [
    ...(onSettings ? [{ key: 'settings', label: 'Inställningar', icon: <Icon as={Settings} />, onSelect: onSettings }] : []),
    ...(onPublicPage ? [{ key: 'public', label: 'Publik sida', icon: <Icon as={ExternalLink} />, onSelect: onPublicPage }] : []),
    ...(onDelete ? [{ key: 'delete', label: 'Radera mark', icon: <Icon as={Trash2} />, danger: true, onSelect: onDelete }] : []),
  ];
  // Desktop visar Inställningar och Publik sida som knappar; menyn tar resten.
  const menuItems = mob ? actions : actions.filter((a) => a.key === 'delete');
  const moreMenu = menuItems.length > 0 && (
    <Menu align="right" items={menuItems} trigger={(p) => (
      <IconButton {...p} label="Fler åtgärder för marken" variant={mob ? 'plain' : 'filled'}><Icon as={MoreHorizontal} /></IconButton>
    )} />
  );
  const stickyTop = mob ? `calc(${MOBILE_BAR_H}px + env(safe-area-inset-top, 0px))` : `calc(${NAV_MAIN_H + NAV_TABS_H}px + env(safe-area-inset-top, 0px))`;
  const gutter = mob ? '0 16px' : '0 clamp(16px,3vw,32px)';
  const tabRow = (
    <div style={{ position: 'sticky', top: stickyTop, zIndex: 500, background: mob ? T.surface : T.bg }}>
      <div style={{ maxWidth: 1264, margin: '0 auto', padding: gutter }}>
        <Tabs items={tabs} value={tab} onChange={onTab} label={`Flikar för ${mark.name}`} idPrefix="mark-tab" maxVisible={maxVisible} fit={!mob} />
        {subBar}
      </div>
    </div>
  );

  if (mob) {
    return (
      <>
        <div style={{ position: 'sticky', top: 0, zIndex: 501, background: T.surface, paddingTop: 'env(safe-area-inset-top, 0px)', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ height: MOBILE_BAR_H, display: 'flex', alignItems: 'center', gap: 4, padding: '0 6px' }}>
            <button type="button" onClick={onBack} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, minHeight: 44, padding: '0 10px 0 6px', border: 'none', borderRadius: 999,
              background: 'transparent', color: T.text, fontFamily: 'inherit', fontSize: 15, fontWeight: 700, cursor: 'pointer',
            }}><Icon as={ChevronLeft} size={22} />Min jakt</button>
            <div data-testid="mark-bar-title" aria-hidden={!titleHidden} style={{
              flex: 1, minWidth: 0, textAlign: 'center', fontSize: 15, fontWeight: 700, color: T.text,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              opacity: titleHidden ? 1 : 0, transition: 'opacity .15s',
            }}>{mark.name}</div>
            {moreMenu}
          </div>
        </div>
        <div style={{ background: T.surface, padding: '16px 16px 2px' }}>
          <h1 ref={titleRef} style={{ ...TYPE.page, fontSize: 24, color: T.text, margin: 0, overflowWrap: 'anywhere' }}>{mark.name}</h1>
          <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 4, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>{sub}</span>{roleLabel && <Badge tone="accent">{roleLabel}</Badge>}
          </div>
        </div>
        {tabRow}
        {notice}
      </>
    );
  }

  // Fragment: flikraden måste ligga direkt i sidan för att position:sticky ska gälla hela sidan.
  return (
    <>
      <div style={{ maxWidth: 1264, margin: '0 auto', padding: `calc(${NAV_MAIN_H + NAV_TABS_H + 20}px + env(safe-area-inset-top, 0px)) clamp(16px,3vw,32px) 0` }}>
        <nav aria-label="Brödsmulor" style={{ ...TYPE.small, color: T.textMuted, display: 'flex', alignItems: 'center', gap: 6, margin: '-6px 0' }}>
          <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', padding: 0, minHeight: 44, color: T.textSub, font: 'inherit', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}>Min jakt</button>
          <span aria-hidden>/</span>
          <span aria-current="page">{mark.name}</span>
        </nav>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', margin: '8px 0 18px' }}>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ ...TYPE.page, color: T.text, margin: 0 }}>{mark.name}</h1>
            <div style={{ fontSize: 14, color: T.textMuted, marginTop: 6, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>{sub}</span>{roleLabel && <Badge tone="accent">{roleLabel}</Badge>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {onPublicPage && <Button variant="secondary" pill icon={<Icon as={ExternalLink} size={18} />} onClick={onPublicPage}>Publik sida</Button>}
            {onSettings && <Button variant="secondary" pill icon={<Icon as={Settings} size={18} />} onClick={onSettings}>Inställningar</Button>}
            {moreMenu}
          </div>
        </div>
      </div>
      {tabRow}
      {notice}
    </>
  );
}
